import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Motion";
import { api } from "@/lib/api";
import { img } from "@/lib/content";

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [meta, setMeta] = useState({ locations: [], sectors: [], types: [] });
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [sector, setSector] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (location) params.location = location;
      if (sector) params.sector = sector;
      if (type) params.type = type;
      const r = await api.get("/jobs", { params });
      setJobs(r.data.jobs || []);
    } catch {
      toast.error("Could not load opportunities. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [q, location, sector, type]);

  useEffect(() => {
    const t = setTimeout(fetchJobs, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchJobs, q]);

  const selectCls =
    "nx-input cursor-pointer appearance-none bg-obsidian pr-8 [&>option]:bg-obsidian";

  return (
    <>
      <Seo
        title="Jobs — NEXREQ UAE & GCC"
        siteName="NEXREQ"
        description="Executive and professional opportunities across the UAE, Saudi Arabia, Qatar and the wider GCC."
      />
      <PageHero
        eyebrow="Opportunities"
        lines={["Roles Worth", "Moving For."]}
        description="A curated selection of executive and professional mandates across the UAE and GCC. Every role is a live mandate with a named consultant behind it."
        image={img("photo-1518684079-3c830dcef090", 1920)}
      />

      <section className="border-t border-white/[0.08] bg-obsidian py-10" data-testid="jobs-filters">
        <div className="nx-container grid gap-4 lg:grid-cols-[2fr_1fr_1fr_1fr]">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              className="nx-input pl-11"
              placeholder="Search by title, sector or reference"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="jobs-search-input"
            />
          </div>
          <select className={selectCls} value={location} onChange={(e) => setLocation(e.target.value)} data-testid="jobs-location-filter">
            <option value="">All Locations</option>
            {meta.locations.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <select className={selectCls} value={sector} onChange={(e) => setSector(e.target.value)} data-testid="jobs-sector-filter">
            <option value="">All Sectors</option>
            {meta.sectors.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select className={selectCls} value={type} onChange={(e) => setType(e.target.value)} data-testid="jobs-type-filter">
            <option value="">All Types</option>
            {meta.types.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </section>

      <section className="bg-obsidian pb-28" data-testid="jobs-list">
        <div className="nx-container">
          <p className="py-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/35" data-testid="jobs-count">
            {loading ? "Loading mandates…" : `${jobs.length} open mandate${jobs.length === 1 ? "" : "s"}`}
          </p>
          {!loading && jobs.length === 0 && (
            <div className="border border-white/10 bg-surface p-12 text-center" data-testid="jobs-empty">
              <p className="font-heading text-xl font-light text-white/70">No mandates match your search.</p>
              <p className="mt-3 text-sm text-white/40">
                Adjust your filters — or{" "}
                <Link to="/upload-resume" className="text-emerald-400 hover:underline">
                  upload your resume
                </Link>{" "}
                and we will contact you when a matching mandate opens.
              </p>
            </div>
          )}
          {jobs.map((j, i) => (
            <Reveal key={j.id} delay={Math.min(i, 5) * 0.05} y={20}>
              <Link
                to={`/jobs/${j.id}`}
                className="group grid gap-3 border-t border-white/10 px-2 py-8 transition-colors duration-300 hover:bg-panel/[0.03] md:grid-cols-[150px_1fr_220px_32px] md:items-center md:gap-8"
                data-testid={`job-row-${j.id}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
                  <p>{j.reference}</p>
                  <p className="mt-1 text-emerald-500/80">{j.type}</p>
                  {j.demo && <p className="mt-1 text-white/30">Demo listing</p>}
                </div>
                <div>
                  <h2 className="font-heading text-xl font-light text-white transition-colors group-hover:text-emerald-400 lg:text-2xl">
                    {j.title}
                  </h2>
                  <p className="mt-2 line-clamp-1 text-sm text-white/45">{j.summary}</p>
                </div>
                <div className="text-sm text-white/50">
                  <p>{j.location}</p>
                  <p className="mt-1 text-white/30">{j.salary}</p>
                </div>
                <ArrowUpRight
                  className="text-white/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-emerald-400"
                  size={20}
                />
              </Link>
            </Reveal>
          ))}
          <div className="border-t border-white/10" />
        </div>
      </section>
    </>
  );
};

export default Jobs;
